import React, { useState, useRef, useEffect } from 'react';
import api from '../api/api';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';

type OrderSettings = {
  autoAccept: boolean;
  allowCancellation: boolean;
  allowModification: boolean;
};

interface OrderPreferencesProps {
  vendorId: string;
  settings: OrderSettings;
  onToggleSetting: (key: keyof OrderSettings) => void;
  buttonClassName?: string;
  themeButton?: {
    bg?: string;
    text?: string;
    hover?: string;
  };
  dropdownAlign?: 'left' | 'right';
}

const OrderPreferences: React.FC<OrderPreferencesProps> = ({
  vendorId,
  settings,
  onToggleSetting,
  buttonClassName = '',
  themeButton,
  dropdownAlign = 'right',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [localSettings, setLocalSettings] = useState(settings);
  const { theme } = useTheme();

  // Use provided themeButton or get from global theme
  const buttonTheme = themeButton || theme?.button?.primary || {
    bg: '#B37A4C',
    text: '#FFFFFF',
    hover: '#C98B5F'
  };

  const [hoverState, setHoverState] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get(`/vendors/${vendorId}/settings`);
        setLocalSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };

    if (vendorId) {
      fetchSettings();
    }
  }, [vendorId]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const onSaveSettings = async () => {
    if (!vendorId) return;
    try {
      await api.put(`/vendors/${vendorId}/settings`, localSettings);
      Object.keys(localSettings).forEach((key) => {
        onToggleSetting(key as keyof OrderSettings);
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };

  const onResetSettings = () => {
    setLocalSettings(settings);
  };

  return (
    <div className="relative inline-block text-left">
      <button
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        className={`inline-flex items-center font-medium text-sm ${buttonClassName}`}
        style={{ 
          color: buttonTheme.text, 
          backgroundColor: hoverState ? buttonTheme.hover : buttonTheme.bg 
        }}
        aria-haspopup="true"
        aria-expanded={isDropdownOpen}
        onMouseOver={() => setHoverState(true)}
        onMouseOut={() => setHoverState(false)}
      >
        Order Preferences
        <ChevronDownIcon 
          className="w-4 h-4 ml-1" 
          aria-hidden="true" 
          style={{ color: buttonTheme.text }} 
        />
      </button>

      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 ${dropdownAlign === 'left' ? 'left-0' : 'right-0'} mt-2 ${dropdownAlign === 'left' ? 'w-60' : 'w-64'} ${dropdownAlign === 'left' ? 'origin-top-left' : 'origin-top-right'} border border-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 transition ease-out duration-100`}
          role="menu"
          aria-orientation="vertical"
          style={{ 
            backgroundColor: theme?.components?.card?.background || '#FFFFFF',
            borderColor: theme?.components?.card?.border || '#E0E0E0' 
          }}
        >
          <div className="py-1">
            <label className="flex items-center justify-between px-4 py-2 text-sm" 
                  style={{ color: theme?.text || '#3E3E3E' }}>
              <span>Auto-accept orders</span>
              <input
                type="checkbox"
                checked={localSettings.autoAccept}
                onChange={() =>
                  setLocalSettings((prev) => ({
                    ...prev,
                    autoAccept: !prev.autoAccept,
                  }))
                }
              />
            </label>
            <label className="flex items-center justify-between px-4 py-2 text-sm" 
                  style={{ color: theme?.text || '#3E3E3E' }}>
              <span>Allow cancellation</span>
              <input
                type="checkbox"
                checked={localSettings.allowCancellation}
                onChange={() =>
                  setLocalSettings((prev) => ({
                    ...prev,
                    allowCancellation: !prev.allowCancellation,
                  }))
                }
              />
            </label>
            <label className="flex items-center justify-between px-4 py-2 text-sm" 
                  style={{ color: theme?.text || '#3E3E3E' }}>
              <span>Allow modification</span>
              <input
                type="checkbox"
                checked={localSettings.allowModification}
                onChange={() =>
                  setLocalSettings((prev) => ({
                    ...prev,
                    allowModification: !prev.allowModification,
                  }))
                }
              />
            </label>
            <div className="flex justify-between px-4 py-2 mt-2">
              <button
                className="px-3 py-2 text-sm font-medium rounded-md"
                style={{ 
                  backgroundColor: buttonTheme.bg, 
                  color: buttonTheme.text 
                }}
                onClick={onSaveSettings}
              >
                Save
              </button>
              <button
                className="px-3 py-2 text-sm font-medium rounded-md"
                style={{ 
                  backgroundColor: theme?.button?.secondary?.bg || '#F8EEDC', 
                  color: theme?.button?.secondary?.text || '#3E3E3E'
                }}
                onClick={onResetSettings}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPreferences;
